/**
 * Top-nav live "Giveaways" pill.
 *
 * Polls /api/giveaways every 60s. If there's at least one active
 * giveaway, renders a small Gift-icon badge that deep-links to the
 * player Giveaways page:
 *
 *     🎁  2 live   →  /giveaways
 *
 * Renders nothing when there are no open giveaways so the nav stays
 * clean. Hidden for non-logged-in users — they can't enter anyway
 * (the page itself shows a login prompt).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 60_000;

export function LiveGiveawaysPill() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/giveaways");
        if (!alive) return;
        const open = (data?.giveaways || []).filter((g) => g.is_open).length;
        setCount(open);
      } catch {
        if (alive) setCount(0);
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  if (!user || count <= 0) return null;

  return (
    <Link
      to="/giveaways"
      className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--gold)]/40 bg-[rgba(255,215,128,0.08)] text-[var(--gold)] text-[10px] uppercase tracking-wider hover:bg-[rgba(255,215,128,0.16)] transition-colors"
      data-testid="live-giveaways-pill"
      title={`${count} active giveaway${count === 1 ? "" : "s"}`}
    >
      <Gift size={12} className="animate-pulse" />
      <span>{count} live</span>
    </Link>
  );
}
